import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import { ArrowUpRight, Clock, Calendar, Shield, Receipt, Books, Scales, ChatsTeardrop } from "@phosphor-icons/react";

const SERVICES = [
    { icon: Receipt, name: "Tax Filing", blurb: "Personal and small-business filings handled with precision." },
    { icon: Books, name: "Bookkeeping", blurb: "Monthly books, reconciliations, financial clarity." },
    { icon: Scales, name: "Audit", blurb: "Independent review of books and financial records." },
    { icon: ChatsTeardrop, name: "Consultation", blurb: "Practical advisory for small-business owners." },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-bone text-ink">
            <SiteHeader active="home" />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-divider" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7 animate-fade-up">
                        <p className="text-xs uppercase tracking-[0.25em] text-ash mb-6">A Zakir Dema Company · Est. 2010</p>
                        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-ink">
                            Every number,<br />
                            <span className="italic text-moss">accounted for.</span>
                        </h1>
                        <p className="mt-8 text-base md:text-lg text-ash max-w-xl leading-relaxed">
                            Schedule a 30-minute appointment with a bookkeeper at Noble Accounting Services.
                            Available Monday through Friday, 12 noon to 5 in the afternoon.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link
                                to="/book"
                                className="group inline-flex items-center gap-3 bg-moss text-bone px-7 py-4 hover:bg-moss-hover transition-colors"
                                data-testid="cta-book-hero"
                            >
                                <span className="text-sm uppercase tracking-[0.2em]">Book an appointment</span>
                                <ArrowUpRight size={18} weight="light" className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </Link>
                            <Link
                                to="/kiosk"
                                className="inline-flex items-center gap-2 border border-ink px-7 py-4 text-sm uppercase tracking-[0.2em] hover:bg-ink hover:text-bone transition-colors"
                                data-testid="cta-kiosk-hero"
                            >
                                Kiosk check-in
                            </Link>
                        </div>
                    </div>

                    <div className="md:col-span-5 relative">
                        <div className="relative aspect-[4/5] overflow-hidden border border-divider">
                            <img
                                src="https://images.unsplash.com/photo-1713461983836-de0a45009424?auto=format&fit=crop&w=1200&q=80"
                                alt="Bookkeeper at a desk working with a calculator"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="hidden md:block absolute -bottom-5 -left-5 bg-bone border border-divider p-5 w-56">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-ash mb-2">Office hours</div>
                            <div className="font-mono text-sm text-ink">Mon–Fri · 12:00–17:00</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="border-b border-divider" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
                        <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-ash mb-3">Services</p>
                            <h2 className="font-serif text-3xl md:text-5xl font-light">Four disciplines. One firm.</h2>
                        </div>
                        <div className="text-sm text-ash max-w-sm">
                            Each appointment is booked in thirty-minute blocks — enough time for clarity, not enough for dawdling.
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-divider">
                        {SERVICES.map((s, i) => (
                            <div
                                key={s.name}
                                className="service-card border-r border-b border-divider p-8 bg-bone"
                                data-testid={`service-card-${i}`}
                            >
                                <s.icon size={32} weight="light" className="text-moss" />
                                <h3 className="font-serif text-2xl mt-6">{s.name}</h3>
                                <p className="text-sm text-ash mt-3 leading-relaxed">{s.blurb}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Booking CTA */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6 relative order-2 md:order-1">
                        <div className="aspect-[5/4] overflow-hidden border border-divider">
                            <img
                                src="https://images.unsplash.com/photo-1775163024488-e88e4a71179f?auto=format&fit=crop&w=1200&q=80"
                                alt="Two people reviewing financial documents together at a table"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="md:col-span-6 order-1 md:order-2">
                        <p className="text-xs uppercase tracking-[0.25em] text-ash mb-4">How it works</p>
                        <h2 className="font-serif text-3xl md:text-5xl font-light mb-8">Three steps, no phone calls.</h2>
                        <ol className="space-y-6">
                            {[
                                { n: "01", t: "Pick a service", d: "Choose from tax, books, audit or counsel." },
                                { n: "02", t: "Choose a time", d: "Any open 30-minute slot, Monday to Friday." },
                                { n: "03", t: "Confirm", d: "We email you a reference and see you on the day." },
                            ].map((step) => (
                                <li key={step.n} className="flex gap-6 border-t border-divider pt-6">
                                    <div className="font-mono text-sm text-moss">{step.n}</div>
                                    <div>
                                        <div className="font-serif text-xl">{step.t}</div>
                                        <div className="text-sm text-ash mt-1">{step.d}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                        <Link
                            to="/book"
                            className="mt-10 inline-flex items-center gap-3 bg-moss text-bone px-7 py-4 hover:bg-moss-hover transition-colors"
                            data-testid="cta-book-footer"
                        >
                            <Calendar size={18} weight="light" />
                            <span className="text-sm uppercase tracking-[0.2em]">Begin booking</span>
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-divider bg-stone/40">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row justify-between gap-6 text-sm text-ash">
                    <div className="flex items-center gap-2">
                        <Shield size={16} weight="light" /> Noble Accounting Services — A Zakir Dema Company
                    </div>
                    <div className="flex items-center gap-6">
                        <span className="inline-flex items-center gap-2"><Clock size={14} /> Mon–Fri 12:00–17:00</span>
                        <Link to="/staff/login" className="hover:text-ink" data-testid="footer-staff">Staff</Link>
                        <Link to="/kiosk" className="hover:text-ink" data-testid="footer-kiosk">Kiosk</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
